import { useCallback, useEffect, useState } from 'react'
import { Loader2, RotateCcw, AlertCircle } from 'lucide-react'
import {
  QuizRenderer,
  MultiChoiceQuizForDiary,
  FillBlankQuizForDiary,
  SelectTextQuizForDiary,
  isNewUiQuiz,
  substituteVariables,
} from '@quebon/shared-learning'
import type {
  QuizScript,
  QuizSolveRequest,
  SolveResult,
} from '@quebon/shared-learning'
import type {
  QuizData,
  QuizSolveResult,
  MultiChoiceQuizData,
  FillBlankQuizData,
  SelectTextQuizData,
} from '@quebon/shared-learning'
import { quizScriptApi } from '@/api/quizScript'
import { formatQuizValidationIssues, getQuizValidationIssues } from '@/pages/books/fullBookPreview/validateSampleVars'
import { QuizReviewMetadata } from './QuizReviewMetadata'

interface Props {
  quizId: string
  revealOnly?: boolean
}

function toLegacyQuiz(q: QuizScript | null): QuizData | null {
  if (!q) return null
  const raw = q as unknown as Record<string, unknown>
  const qd = (raw.quizData as QuizData) || (q as unknown as QuizData)
  return {
    ...qd,
    type: q.type || qd.type,
    title: q.title || qd.title,
    problem: q.problem || qd.problem,
    vars: q.vars || qd.vars,
    image: q.image || qd.image,
    imageMobile: q.imageMobile || qd.imageMobile,
  } as QuizData
}

function buildRevealResult(q: QuizScript): QuizSolveResult {
  const desc = q.description
  // 해설 텍스트의 #{var} placeholder를 concrete vars(getVarsWithSampleValueMap)로 치환.
  // problem/answers는 서버에서 이미 치환되지만 description은 원본으로 내려온다.
  const vars = q.vars
  const substitutedText =
    desc?.text && vars && Object.keys(vars).length > 0
      ? substituteVariables(desc.text, vars as Record<string, unknown>)
      : desc?.text
  return {
    correct: true,
    corrects: [],
    correctAnswers: (q.correctAnswers ?? []).map(a => String(a)),
    elapsedTime: 0,
    description: desc
      ? {
          text: substitutedText ?? undefined,
          image: (() => {
            const img = (desc as Record<string, unknown>).image
            if (typeof img === 'string') return { signedUrl: img }
            if (img && typeof img === 'object') return img as { signedUrl: string }
            return undefined
          })(),
          imageMobile: (() => {
            const img = (desc as Record<string, unknown>).imageMobile
            if (typeof img === 'string') return { signedUrl: img }
            if (img && typeof img === 'object') return img as { signedUrl: string }
            return undefined
          })(),
          desmos: desc.desmos ?? undefined,
        }
      : undefined,
  }
}

function transformAnswers(
  type: QuizData['type'],
  answers: string[],
  legacy: QuizData
): (string | number | boolean)[] {
  switch (type) {
    case 'fill_blank':
      return answers
    case 'multi_choice': {
      const quiz = legacy as MultiChoiceQuizData
      return answers
        .map(key => {
          const idx = quiz.choices.findIndex(c => c.key === key)
          return idx >= 0 ? idx + 1 : 0
        })
        .filter(idx => idx > 0)
    }
    case 'select_text':
      return answers.map(a => {
        const n = parseInt(a, 10)
        return isNaN(n) ? 0 : n
      })
    case 'boolean':
      return answers.map(a => a === 'true')
    case 'drag_text':
    case 'coloring':
    case 'counting':
    case 'image_select':
    case 'ordering':
    case 'pairing':
      return answers
    default:
      return []
  }
}

export function QuizPreviewPanel({ quizId, revealOnly = false }: Props) {
  const [quizData, setQuizData] = useState<QuizScript | null>(null)
  const [solveResult, setSolveResult] = useState<QuizSolveResult | null>(null)
  const [submittedAnswers, setSubmittedAnswers] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [solving, setSolving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resetKey, setResetKey] = useState(0)
  const [revealAnswers, setRevealAnswers] = useState(true)
  const shouldRevealAnswers = revealOnly || revealAnswers

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setQuizData(null)
    setSolveResult(null)
    setSubmittedAnswers([])

    quizScriptApi
      // 검수 메타데이터는 정답 표시 토글과 무관하게 항상 받아온다.
      // 실제 정답/해설 렌더 여부는 solveResult 주입으로 계속 제어한다.
      .getQuiz(quizId, false, undefined, true, true)
      .then(data => {
        if (cancelled) return
        setQuizData(data)
        if (shouldRevealAnswers) setSolveResult(buildRevealResult(data))
      })
      .catch(err => {
        console.error('getQuiz failed', err)
        if (!cancelled) setError('퀴즈를 불러오지 못했습니다.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [quizId, resetKey, shouldRevealAnswers])

  const handleSolve = useCallback(
    async (
      _quizId: string | number,
      answers: string[],
      quizVars: Record<string, unknown>,
      elapsedTime: number,
    ) => {
      if (shouldRevealAnswers) return
      if (!quizData || solving) return
      const legacy = toLegacyQuiz(quizData)
      if (!legacy) return

      setSubmittedAnswers(answers)
      setSolving(true)

      try {
        const request: QuizSolveRequest = {
          answers: transformAnswers(legacy.type, answers, legacy),
          vars: quizVars,
          elapsed: elapsedTime,
        }
        const apiResult: SolveResult = await quizScriptApi.solve(
          String(quizData.quizId ?? quizId),
          request,
        )
        const result: QuizSolveResult = {
          correct: apiResult.correct,
          corrects: apiResult.corrects.map(c => {
            const v = c as unknown
            return Boolean(v === true || v === 'true')
          }),
          correctAnswers: (apiResult.correctAnswers ?? []).map(a => String(a)),
          elapsedTime,
          description: apiResult.description
            ? {
                text: apiResult.description.text ?? undefined,
                image: (() => {
                  const img = (apiResult.description as Record<string, unknown>).image
                  if (typeof img === 'string') return { signedUrl: img }
                  if (img && typeof img === 'object') return img as { signedUrl: string }
                  return undefined
                })(),
                imageMobile: (() => {
                  const img = (apiResult.description as Record<string, unknown>).imageMobile
                  if (typeof img === 'string') return { signedUrl: img }
                  if (img && typeof img === 'object') return img as { signedUrl: string }
                  return undefined
                })(),
                desmos: apiResult.description.desmos ?? undefined,
              }
            : undefined,
        }
        setSolveResult(result)
      } catch (err) {
        console.error('solve failed', err)
        setSolveResult({
          correct: false,
          corrects: [],
          correctAnswers: [],
          elapsedTime,
        })
      } finally {
        setSolving(false)
      }
    },
    [quizData, solving, quizId, shouldRevealAnswers],
  )

  const handleReset = useCallback(() => {
    setResetKey(k => k + 1)
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <AlertCircle className="w-8 h-8 text-rose-400" />
        <p className="text-sm text-rose-600">{error}</p>
        <button
          onClick={handleReset}
          className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50"
        >
          다시 시도
        </button>
      </div>
    )
  }

  if (!quizData) return null

  const legacy = toLegacyQuiz(quizData)
  if (!legacy) return null

  const currentId = String(quizData.quizId ?? quizId)
  const newUi = isNewUiQuiz(currentId)
  const renderKey = `${currentId}-${resetKey}-${shouldRevealAnswers ? 'reveal' : 'solve'}-${solveResult ? 'solved' : 'unsolved'}`
  const validationIssues = getQuizValidationIssues(quizData)

  return (
    <div className="relative">
      {!revealOnly ? (
        <>
          <button
            onClick={handleReset}
            className="absolute top-0 right-0 z-10 flex items-center gap-1 px-2 py-1 text-xs text-slate-500 bg-white border border-slate-200 rounded-md hover:bg-slate-50"
            title="리셋"
          >
            <RotateCcw className="w-3 h-3" />
            리셋
          </button>
          <button
            onClick={() => setRevealAnswers(v => !v)}
            className="absolute top-0 right-16 z-10 flex items-center gap-1 px-2 py-1 text-xs text-slate-500 bg-white border border-slate-200 rounded-md hover:bg-slate-50"
            title="정답·해설 표시"
          >
            {shouldRevealAnswers ? '정답 숨기기' : '정답 보기'}
          </button>
        </>
      ) : null}

      {validationIssues.length > 0 && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <div className="flex items-center gap-2 font-medium">
            <AlertCircle className="h-4 w-4" />
            <span>sample value 또는 answersorder가 누락되었습니다.</span>
          </div>
          <div className="mt-1 text-xs">누락: {formatQuizValidationIssues(validationIssues)}</div>
        </div>
      )}

      {newUi ? (
        <div className="quiz-new-ui min-h-[300px] rounded-xl border-2 border-amber-300 bg-amber-50/60 p-4">
          {legacy.type === 'fill_blank' && (
            <FillBlankQuizForDiary
              key={renderKey}
              quizId={currentId}
              quiz={legacy as FillBlankQuizData}
              onSolve={handleSolve}
              solveResult={solveResult}
              showReallyTrue={shouldRevealAnswers}
              isViewSolved={shouldRevealAnswers}
              initialAnswers={submittedAnswers.length > 0 ? submittedAnswers : undefined}
            />
          )}
          {legacy.type === 'multi_choice' && (
            <MultiChoiceQuizForDiary
              key={renderKey}
              quizId={currentId}
              quiz={legacy as MultiChoiceQuizData}
              onSolve={handleSolve}
              solveResult={solveResult}
              showReallyTrue={shouldRevealAnswers}
              isViewSolved={shouldRevealAnswers}
              initialAnswers={submittedAnswers.length > 0 ? submittedAnswers : undefined}
            />
          )}
          {legacy.type === 'select_text' && (
            <SelectTextQuizForDiary
              key={renderKey}
              quizId={currentId}
              quiz={legacy as SelectTextQuizData}
              onSolve={handleSolve}
              solveResult={solveResult}
              showReallyTrue={shouldRevealAnswers}
              isViewSolved={shouldRevealAnswers}
              initialAnswers={submittedAnswers.length > 0 ? submittedAnswers : undefined}
            />
          )}
        </div>
      ) : (
        <div className="min-h-[300px]">
          <QuizRenderer
            key={renderKey}
            quizType={legacy.type}
            quizId={currentId}
            quiz={legacy}
            onSolve={handleSolve}
            solveResult={solveResult}
            showReallyTrue={shouldRevealAnswers}
            isViewSolved={shouldRevealAnswers}
            useKeypad={false}
            initialAnswers={submittedAnswers.length > 0 ? submittedAnswers : undefined}
          />
        </div>
      )}

      <QuizReviewMetadata
        correctCriteria={quizData.correctCriteria}
        checkPoint={quizData.checkPoint}
      />

      {solving && (
        <div className="flex justify-center items-center gap-2 mt-4 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>채점 중...</span>
        </div>
      )}
    </div>
  )
}
